import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  Col,
  Badge,
} from 'reactstrap';
import Header from 'components/Headers/Header.js';
import { FaRegListAlt } from 'react-icons/fa';
import api from 'services/api';
import { Checkbox, FormControlLabel, Tooltip } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { AutoSizer, Column, Table as Tbl } from 'react-virtualized';
import {
  enumStatus,
  iMaterial,
  iMaterialLista,
} from 'models/MaterialAcessorio';
import ModalMaterialAcessorioAprovacao from './components/ModalMaterialAcessorioAprovacao';

const MateriaisAcessorioAprovacao: React.FC = () => {
  const [listaMaterial, setListaMaterial] = useState([] as iMaterialLista[]);
  const [loading, setLoading] = useState(false);
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [isModalAcessorioOpen, setIsModalAcessorioOpen] = useState(false);

  const [registroUsar, setRegistroUsar] = useState<{
    propostaVersaoId?: number;
    orcamentoId?: number;
    orcamentoMaterialId?: number;
  }>({
    propostaVersaoId: 0,
    orcamentoMaterialId: 0,
    orcamentoId: 0,
  });

  async function carregarDados() {
    setLoading(true);
    const toastId = toast.loading('Buscando registros');
    const response = await api.get(`/material-acessorio?todos=${mostrarTodos}`);

    toast.dismiss(toastId);
    const lista = response.data as iMaterial[];

    const formatado = lista.map(item => {
      return {
        id: item.id,
        orcamentoId: item.orcamentoId,
        orcamentoMaterialId: item.orcamentoMaterialId,
        propostaMaterialAvulsoId: item.propostaMaterialAvulsoId,
        status: item.status,
        statusCor:
          // eslint-disable-next-line no-nested-ternary
          item.status === 'S'
            ? `success`
            : item.status === 'A'
            ? `warning`
            : `danger`,
        statusNome: enumStatus[String(item.status ?? 'P')],
        descricao: item.descricao,
        descricaoAprovacao: item.descricaoAprovacao,
        perfil: item.perfil.nome,
        material: `${item.material.codigoSap} - ${item.material.descricao}`,
        usuarioAprovacao: item.usuarioAprovacao
          ? String(item.usuarioAprovacao.username).toUpperCase()
          : '',
        usuarioSolicitante: String(
          item.usuarioSolicitante.username,
        ).toUpperCase(),
        codigoOportunidadeDomsge: item.orcamento.codigoOportunidadeDomsge ?? '',
        clienteCodigo: item.orcamento.pessoa.codigoDomsge ?? '',
        clienteNome: item.orcamento.pessoa.razaoSocial ?? '',
      };
    }) as iMaterialLista[];

    setListaMaterial(formatado);
    setLoading(false);
  }

  useEffect(() => {
    carregarDados();
  }, [mostrarTodos]);

  return (
    <>
      <Header showCards={false} />

      <ModalMaterialAcessorioAprovacao
        isOpen={isModalAcessorioOpen}
        setIsOpen={setIsModalAcessorioOpen}
        orcamentoMaterialId={registroUsar.orcamentoMaterialId}
        orcamentoId={registroUsar.orcamentoId}
        propostaVersaoId={registroUsar.propostaVersaoId}
        changeStatus={() => {
          carregarDados();
        }}
      />

      <Container className="mt--7" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col sm="6">
                    <h3 className="mb-0">
                      Lista de Materiais Acessório para Aprovação
                    </h3>
                  </Col>
                  <Col sm="6">
                    <FormControlLabel
                      className="float-right"
                      control={
                        <Checkbox
                          checked={mostrarTodos}
                          onChange={event =>
                            setMostrarTodos(event.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Mostrar todos os registros"
                    />
                  </Col>
                </Row>
              </CardHeader>

              {listaMaterial && listaMaterial.length > 0 && (
                <div className="mt-4">
                  <div style={{ height: '600px', width: '100%' }}>
                    <AutoSizer>
                      {({ height, width }) => (
                        <Tbl
                          gridStyle={{ outline: 'none' }}
                          width={width}
                          height={height}
                          headerHeight={30}
                          rowHeight={70}
                          rowCount={listaMaterial.length}
                          rowGetter={({ index }: { index: number }) =>
                            listaMaterial[index]
                          }
                        >
                          <Column width={50} label="#" dataKey="id" />
                          <Column
                            label="Status"
                            dataKey="status"
                            width={50}
                            flexGrow={1}
                            cellRenderer={({ rowData, rowIndex }) => {
                              return (
                                <Badge color={`${rowData.statusCor}`}>
                                  {rowData.statusNome}
                                </Badge>
                              );
                            }}
                          />

                          <Column
                            label="Configuração"
                            dataKey="configuracao"
                            width={50}
                            flexGrow={1}
                            cellRenderer={({ rowData }) => {
                              const isConfiguracao =
                                rowData.orcamentoId ?? false;

                              const link = rowData.orcamentoId
                                ? `/admin/configuracao/${rowData.orcamentoId}`
                                : ``;

                              return (
                                <>
                                  <Tooltip title="Navegar para o registro">
                                    <Link
                                      color="default"
                                      to={link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {isConfiguracao
                                        ? `#${rowData.orcamentoId} GNF: ${rowData.codigoOportunidadeDomsge}`
                                        : ``}
                                    </Link>
                                  </Tooltip>
                                </>
                              );
                            }}
                          />

                          <Column
                            label="Material"
                            dataKey="material"
                            width={100}
                            flexGrow={1}
                            cellRenderer={({ cellData }) => (
                              <div
                                style={{
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  lineHeight: '1.4',
                                  display: 'block',
                                }}
                              >
                                {cellData}
                              </div>
                            )}
                          />

                          <Column
                            width={50}
                            flexGrow={1}
                            label="Solicitante"
                            dataKey="usuarioSolicitante"
                          />
                          <Column
                            width={50}
                            flexGrow={1}
                            label="Atuado por"
                            dataKey="usuarioAprovacao"
                          />

                          <Column
                            label=""
                            dataKey="acoes"
                            width={150}
                            flexShrink={0}
                            cellRenderer={({ rowData }) => {
                              return (
                                <div className="float-right">
                                  <Tooltip title="Visualizar Requisição">
                                    <Button
                                      size="sm"
                                      className="btn-icon"
                                      color="info"
                                      onClick={() => {
                                        setIsModalAcessorioOpen(true);
                                        setRegistroUsar({
                                          orcamentoId: rowData.orcamentoId,
                                          orcamentoMaterialId:
                                            rowData.orcamentoMaterialId,
                                          propostaVersaoId:
                                            rowData.propostaVersaoId,
                                        });
                                      }}
                                      title="Visualizar Requisição"
                                    >
                                      <FaRegListAlt />
                                    </Button>
                                  </Tooltip>
                                </div>
                              );
                            }}
                          />
                        </Tbl>
                      )}
                    </AutoSizer>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default MateriaisAcessorioAprovacao;
